import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axios from "axios";
import { Platform } from "react-native";
import { jwtDecode } from "jwt-decode";
import {
  LoginPayload,
  LoginResponse,
  LogoutPayload,
  LogoutResponse,
  ResetPasswordPayload,
  SetPasswordResponse,
  SubmitPayload,
  SubmitResponse,
} from "../../models/auth.models";
import { BLOCKS_ENDPOINTS } from "@/src/services/blocks/config";
import { isRealOrgId } from "@/src/constants/roles-permissions.constants";
import {
  clearMeCache,
  fetchMe,
  getCachedOrgId,
  resolveActiveOrgId,
  setActiveOrgId,
  switchOrg,
} from "@/src/services/blocks/session";
import { shouldEndUnrememberedSession } from "@/src/utils/remember-me.utils";
import apiClient from "./axios";
import { secureStorage } from "./secure-storage";
import { queryClient } from "@/src/config/react-query";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const ACTIVE_ORG_KEY = "active_org_id";
const ORG_SELECTED_KEY = "org_selected";
const REMEMBER_ME_KEY = "remember_me";

const isWeb = Platform.OS === "web";

export type WebIdentity = Readonly<{ user_id: string; email: string }>;

let webIdentity: WebIdentity | null = null;
let webActiveOrgId: string | undefined;
let orgHydrated = false;
let identityInFlight: Promise<WebIdentity | null> | null = null;

export async function saveToken(token: string) {
  if (isWeb) return;
  try {
    await secureStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch (e) {
    console.error("Error saving token", e);
  }
}

export async function saveRefreshToken(token: string) {
  if (isWeb) return;
  try {
    await secureStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (e) {
    console.error("Error saving refresh token", e);
  }
}

export async function getToken() {
  if (isWeb) return null;
  try {
    return await secureStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting token", e);
    return null;
  }
}

export async function getRefreshToken() {
  if (isWeb) return null;
  try {
    return await secureStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (e) {
    console.error("Error getting token", e);
    return null;
  }
}

async function hydrateActiveOrg() {
  if (!isWeb || orgHydrated) return;
  orgHydrated = true;
  try {
    webActiveOrgId = (await secureStorage.getItem(ACTIVE_ORG_KEY)) ?? undefined;
  } catch {
    webActiveOrgId = undefined;
  }
}

export async function setActiveOrg(orgId: string | undefined) {
  if (!isWeb) return;
  orgHydrated = true;
  webActiveOrgId = orgId;
  try {
    if (orgId) await secureStorage.setItem(ACTIVE_ORG_KEY, orgId);
    else await secureStorage.removeItem(ACTIVE_ORG_KEY);
  } catch (e) {
    console.error("Error persisting active org", e);
  }
}

export async function setOrgSelected(value: boolean) {
  try {
    if (value) await secureStorage.setItem(ORG_SELECTED_KEY, "true");
    else await secureStorage.removeItem(ORG_SELECTED_KEY);
  } catch (e) {
    console.error("Error persisting org selection", e);
  }
}

export async function getOrgSelected(): Promise<boolean> {
  try {
    return (await secureStorage.getItem(ORG_SELECTED_KEY)) === "true";
  } catch {
    return false;
  }
}

export async function getActiveOrg(): Promise<string | undefined> {
  if (isWeb) {
    await hydrateActiveOrg();
    return webActiveOrgId;
  }
  try {
    const stored = await secureStorage.getItem(ACTIVE_ORG_KEY);
    if (stored) return stored;
    const decoded = await decodeToken();
    return decoded?.org_id ?? undefined;
  } catch {
    return undefined;
  }
}

export async function setRememberMe(value: boolean) {
  try {
    await secureStorage.setItem(REMEMBER_ME_KEY, value ? "true" : "false");
  } catch (e) {
    console.error("Error persisting remember_me", e);
  }
}

export async function getRememberMe(): Promise<boolean> {
  return (await getRememberMePreference()) === true;
}

/**
 * The stored preference, tri-state — `null` when no login has been recorded on
 * this device. See `shouldEndUnrememberedSession` for why the absent case has to
 * stay distinguishable from an explicit `false`.
 */
export async function getRememberMePreference(): Promise<boolean | null> {
  try {
    const stored = await secureStorage.getItem(REMEMBER_ME_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
    return null;
  } catch {
    return null;
  }
}

/**
 * ── The "this run" marker that makes Remember me mean anything ───────────────
 *
 * The session is an HttpOnly cookie whose lifetime the client cannot set, so
 * "don't remember me" cannot be expressed by scoping the cookie. Instead we keep
 * a marker that dies when the user closes the app, and discard the session at
 * the next start if the marker is gone and the toggle was off.
 *
 * Web: a **session cookie** rather than `sessionStorage`. sessionStorage is
 * per-tab, so opening a second tab would look like a fresh browser and sign the
 * user out of the first one. A cookie with no `expires` is shared across tabs
 * and dropped when the browser closes — exactly the lifetime we need.
 *
 * Native: a module variable. The module is re-evaluated on cold start, which is
 * precisely the "app was killed and reopened" event we care about.
 */
const SESSION_ALIVE_KEY = "sc_session_alive";
let nativeSessionAlive = false;

export function markSessionAlive() {
  if (!isWeb) {
    nativeSessionAlive = true;
    return;
  }
  try {
    const secure = window.location?.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${SESSION_ALIVE_KEY}=1; path=/; SameSite=Lax${secure}`;
  } catch {
    // Blocked cookies — `isSessionAlive` reads false and the worst case is a
    // signed-out user, never a session that outlives its permission.
  }
}

export function isSessionAlive(): boolean {
  if (!isWeb) return nativeSessionAlive;
  try {
    return document.cookie
      .split(";")
      .some((part) => part.trim().startsWith(`${SESSION_ALIVE_KEY}=`));
  } catch {
    return false;
  }
}

export function clearSessionAlive() {
  nativeSessionAlive = false;
  if (!isWeb) return;
  try {
    document.cookie = `${SESSION_ALIVE_KEY}=; path=/; Max-Age=0`;
  } catch {
    // nothing to clear
  }
}

/**
 * Enforce "Remember me" at startup: revoke and clear a session the user asked
 * not to be remembered, when this is a fresh browser session / app run.
 *
 * Returns `true` when the session was discarded, so the caller can go straight
 * to the signed-out state instead of resolving an identity it is about to drop.
 */
export async function discardUnrememberedSession(): Promise<boolean> {
  const rememberMe = await getRememberMePreference();
  if (!shouldEndUnrememberedSession({ sessionAlive: isSessionAlive(), rememberMe })) {
    // Same run, or a remembered session — (re-)arm the marker for this run so a
    // later reload is recognised as the same session rather than a new one.
    if (rememberMe !== null) markSessionAlive();
    return false;
  }

  await revokeSession();
  await clearTokens();
  invalidateIdentity();
  clearSessionAlive();
  return true;
}

export function invalidateIdentity() {
  webIdentity = null;
  identityInFlight = null;
  // `/iam/me` is cached for a minute; a caller saying the identity is stale
  // means that copy is stale too.
  clearMeCache();
}

/**
 * v4 has no `/idp/v1/Iam/GetAccount`; `GET /iam/v4/iam/me` is the equivalent and
 * doubles as the "am I signed in?" check (401 → `null`). `fetchMe` uses a raw
 * `fetch` with `credentials: "include"`, so there is no axios 401-refresh
 * interceptor to opt out of the way the v1 call had to.
 */
async function fetchAccount(): Promise<WebIdentity | null> {
  // Deliberately NOT wrapped in a try/catch any more.
  //
  // `fetchMe` returns `null` for a 401 — a real "you are signed out" — and
  // THROWS for everything else: a 500, a gateway timeout, an offline moment, a
  // CORS hiccup. Collapsing both into `null` told `refreshAuth` the session was
  // gone whenever a single request was unlucky, and it then tore down a session
  // that was in fact perfectly valid. See `getSessionStatus`.
  const me = await fetchMe();
  if (!me?.itemId) return null;
  return { user_id: String(me.itemId), email: String(me.email ?? "") };
}

async function ensureIdentity(): Promise<WebIdentity | null> {
  if (webIdentity) return webIdentity;
  if (identityInFlight) return identityInFlight;
  identityInFlight = fetchAccount()
    .then((id) => {
      webIdentity = id;
      return id;
    })
    .finally(() => {
      identityInFlight = null;
    });
  return identityInFlight;
}

export async function clearTokens() {
  // The run marker belongs to the session being cleared; leaving it behind would
  // make the NEXT sign-in's first cold start look like "same run" and skip the
  // Remember me check.
  clearSessionAlive();
  if (isWeb) {
    webIdentity = null;
    identityInFlight = null;
    webActiveOrgId = undefined;
    orgHydrated = true;
    try {
      await secureStorage.multiRemove([
        ACTIVE_ORG_KEY,
        ORG_SELECTED_KEY,
        REMEMBER_ME_KEY,
      ]);
    } catch {}
    return;
  }
  try {
    await secureStorage.multiRemove([
      ACCESS_TOKEN_KEY,
      REFRESH_TOKEN_KEY,
      ORG_SELECTED_KEY,
      REMEMBER_ME_KEY,
    ]);
  } catch (e) {
    console.error("Error clearing tokens", e);
  }
}

/**
 * Revoke the session server-side. `POST /iam/v4/auth/Logout` — verified 2026-07-30
 * to be a real route (401 unauthenticated, so it requires the session it revokes).
 * Replaces the dead v1 `/idp/v1/Authentication/Logout`.
 *
 * Deliberately a bare `axios` call, not `apiClient`: the 401 interceptor would try
 * to refresh a session we are in the middle of throwing away.
 */
export async function webServerLogout() {
  if (!isWeb) return;
  await revokeSession();
}

/**
 * Platform-agnostic form of the above: native carries the same session cookie in
 * the platform cookie store, so a session discarded there has to be revoked too
 * (see `discardUnrememberedSession`).
 */
async function revokeSession() {
  try {
    await axios.post(
      BLOCKS_ENDPOINTS.logout,
      { refreshToken: "" },
      {
        withCredentials: true,
        headers: {
          "x-blocks-key": process.env.EXPO_PUBLIC_X_BLOCKS_KEY as string,
        },
      },
    );
  } catch {}
}

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) return false;
    return exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

/**
 * Three answers, not two.
 *
 * `"unknown"` is the one that was missing, and its absence is what produced the
 * "No name / Unknown role / Access restricted" screen that a reload fixed: any
 * failed `/iam/me` — a 500, a timeout, a dropped connection — was reported as
 * `"signed out"`, so `refreshAuth` cleared a live session out from under a
 * mounted screen. A caller holding a working session must treat `"unknown"` as
 * "ask again later", never as a reason to end it.
 */
export type SessionStatus = "active" | "signed-out" | "unknown";

export const getSessionStatus = async (): Promise<SessionStatus> => {
  if (!isWeb) {
    try {
      const accessToken = await getToken();
      if (accessToken && !isTokenExpired(accessToken)) return "active";
      const refreshToken = await getRefreshToken();
      return refreshToken ? "active" : "signed-out";
    } catch {
      // Storage read failed — that says nothing about the session.
      return "unknown";
    }
  }
  try {
    return (await ensureIdentity()) ? "active" : "signed-out";
  } catch {
    // `/iam/me` did not answer 401, it failed to answer at all.
    return "unknown";
  }
};

export const isAuthenticated = async (): Promise<boolean> =>
  (await getSessionStatus()) === "active";

/**
 * v4: there is no readable token — the session is an HttpOnly cookie. The name is
 * kept because ~10 call sites depend on this shape (`user_id`, `email`, `org_id`,
 * `roles`); only the source changed, from `jwtDecode(token)` to
 * `/iam/v4/iam/me` + the org resolution in `services/blocks/session.ts`.
 *
 * Returns `null` when there is no valid session, exactly as before, so callers
 * that branch on that keep working.
 */
export const decodeToken = async (opts?: { reconcileOrg?: boolean }) => {
  let me = await fetchMe();
  if (!me) return null;

  // The org the app intends to operate in: an explicit stored selection wins,
  // then the sole org, and finally whatever the session is already scoped to.
  //
  // That last fallback matters for a MULTI-org user. `switchOrg` clears the
  // cached org id, and `resolveActiveOrgId` only adopts an org when there is
  // exactly one — so immediately after a switch both come back empty and the org
  // would read as `undefined` for a session that is correctly scoped. The
  // session's own org is the ground truth whenever the client has no selection.
  let org_id =
    getCachedOrgId() ??
    (await resolveActiveOrgId()) ??
    (isRealOrgId(me.organizationId) ? me.organizationId : undefined);

  // ── Reconcile the SERVER's session org with the one resolved above ──────────
  //
  // `/iam/me` returns `roles` scoped to the **session's** org, and a session that
  // has never switched is scoped to the literal `default` tenant — where users
  // hold only `clouduser`, which `appRoles` strips as platform-not-app access. So
  // a super-admin in a real org arrived with an EMPTY role array: "Unknown role"
  // under their name, and "Access restricted" on every gated screen once prod's
  // permissions were actually attached to roles (2026-08-13 — before that
  // `usePermissions` failed open, which is why this surfaced only now).
  //
  // The org was resolved client-side ONLY: `resolveActiveOrgId` silently adopts
  // the sole org from `/organizations/my`, so the app believed it was in Selise
  // while the cookie was still on `default` — and nothing could notice, because
  // this function never read `me.organizationId`. `POST /auth/switch-org` was
  // reachable only from a manual click on the org picker, so every other way in
  // (a remembered `org_selected`, the preferred-org auto-switch, a single-org
  // user adopted silently) kept the default scope and therefore no roles.
  //
  // Opt-in rather than unconditional: ~20 call sites use `decodeToken()` purely
  // to stamp a write with the current org, and a network round-trip plus a
  // session mutation has no business firing on those paths. The auth context —
  // the one place that establishes a session — passes the flag.
  //
  // Fail-soft: if the switch is refused, keep the unswitched session and let the
  // normal org-select flow recover it.
  if (opts?.reconcileOrg && org_id && org_id !== me.organizationId) {
    try {
      await switchOrg(org_id);
      // `switchOrg` clears the cached org id (the cookie it read is now stale),
      // so re-seed it before anything else resolves against an empty cache.
      setActiveOrgId(org_id);
      // Forced: the point of this read is to see the session the switch just
      // produced, not the one cached before it.
      me = (await fetchMe({ force: true })) ?? me;
      org_id = me.organizationId ?? org_id;
    } catch {
      // Keep the session as-is; `session_org_id` below reports the truth.
    }
  }

  return {
    user_id: me.itemId,
    email: me.email,
    org_id,
    /**
     * The org the session is ACTUALLY scoped to, straight from `/iam/me` — as
     * opposed to `org_id`, which is the app's resolved intent and may be a
     * client-side guess. `roles` above belongs to THIS org, so anything asking
     * "has this session got a real org yet?" must read this one.
     */
    session_org_id: me.organizationId,
    roles: me.roles,
  } as any;
};
export const useLoginMutation = (
  options?: Omit<
    UseMutationOptions<LoginResponse, unknown, LoginPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<LoginResponse, unknown, LoginPayload>({
    /**
     * v4 password login — `POST /iam/v4/auth/login`. Replaces the dead v1
     * form-encoded password grant (`POST /idp/v1/Authentication/Token` → 404
     * Application_Not_Found).
     *
     * On success Blocks sets an **HttpOnly session cookie** scoped to the
     * project tenant (`idp_session_id_<projectKey>`; Secure, SameSite) as well
     * as returning tokens in the body. `apiClient` already carries
     * `withCredentials: true`, so the cookie lands and the session works exactly
     * like the SSO path — the body tokens are deliberately ignored rather than
     * stored in JS.
     *
     * Failures are real HTTP errors, so axios rejects and `onError` handles
     * them: 401 `invalid_username_password` for bad credentials, 400
     * `captcha_enabled` when the tenant has captcha turned on. No success-shaped
     * failure to defend against here — see the note on `authLogin` in
     * `blocks/config.ts` for why the previous `/auth-login` path needed that.
     */
    mutationFn: async (payload) => {
      const res = await apiClient.post(
        BLOCKS_ENDPOINTS.authLogin,
        { username: payload.email, password: payload.password },
        { headers: { "Content-Type": "application/json" } },
      );
      return res.data;
    },
    ...options,
  });
};

/**
 * "Forgot password" — send the recovery mail. `POST /iam/v4/auth/recover`,
 * replacing the dead v1 `Iam/Recover`.
 *
 * `projectKey` is gone (the `x-blocks-key` header carries the tenant) and so is
 * `mailPurpose`: v4 accepts the call with `{email}` alone and answers identically
 * with or without it, so the extra field would be an unverified guess about the
 * v4 enum.
 *
 * Verified 2026-07-30: an address that does not exist gets the same
 * `200 {isSuccess: true}` as a real one — the endpoint refuses to leak account
 * existence, so the UI must not treat 200 as "an account was found".
 */
export const useSubmitMutation = (
  options?: Omit<
    UseMutationOptions<SubmitResponse, unknown, SubmitPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<SubmitResponse, unknown, SubmitPayload>({
    mutationFn: (payload) =>
      apiClient
        .post(BLOCKS_ENDPOINTS.recover, { email: payload.email })
        .then((res) => res.data),
    ...options,
  });
};

export const useResetPasswordMutation = (
  options?: Omit<
    UseMutationOptions<SetPasswordResponse, unknown, ResetPasswordPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<SetPasswordResponse, unknown, ResetPasswordPayload>({
    // `POST /iam/v4/auth/reset-password` {code, password, logoutFromAllDevices}
    // — replaces v1 `Iam/ResetPassword`; `projectKey` moved to the header.
    // Verified 2026-07-30: a bad/expired code answers 400
    // `{isSuccess: false, errors: {Code: "The code has expired…"}}`.
    mutationFn: (payload) =>
      apiClient
        .post(BLOCKS_ENDPOINTS.resetPassword, {
          password: payload.password,
          code: payload.code,
          logoutFromAllDevices: true,
        })
        .then((res) => res.data),
    ...options,
  });
};

export const useSetPasswordMutation = (
  options?: Omit<
    UseMutationOptions<SetPasswordResponse, unknown, ResetPasswordPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<SetPasswordResponse, unknown, ResetPasswordPayload>({
    // Account activation / first password set — `POST /iam/v4/auth/activate`
    // {code, password}, replacing v1 `Iam/Activate`. Verified 2026-07-30: `{}`
    // → 400 "'Code' must not be empty", a bogus code → 400 "The code has
    // expired". This is the second half of the invite flow: `users/create` makes
    // the user with an empty password, the mail carries the code, this sets it.
    mutationFn: (payload) =>
      apiClient
        .post(BLOCKS_ENDPOINTS.activate, {
          password: payload.password,
          code: payload.code,
        })
        .then((res) => res.data),
    ...options,
  });
};

export const useLogoutMutation = (
  options?: Omit<
    UseMutationOptions<LogoutResponse, unknown, LogoutPayload>,
    "mutationFn"
  >,
) => {
  return useMutation<LogoutResponse, unknown, LogoutPayload>({
    // Signing out must work offline too (the global offline guard skips it).
    meta: { offline: "allow" },
    // `POST /iam/v4/auth/Logout` (see `webServerLogout`), replacing the dead v1
    // `/idp/v1/Authentication/Logout`. `refreshToken` is only meaningful on
    // native; on web the session being revoked is the cookie.
    mutationFn: (payload) =>
      apiClient
        .post(
          BLOCKS_ENDPOINTS.logout,
          { refreshToken: payload.refreshToken ?? "" },
          {
            headers: {
              Authorization: `Bearer ${payload.accessToken}`,
            },
          },
        )
        .then((res) => res.data),
    onSuccess: () => {
      clearTokens();
      queryClient.clear();
    },
    ...options,
  });
};

/*
 * Four v1 exports were deleted here rather than ported, all callerless and all
 * built on routes that now 404 `Application_Not_Found`:
 *
 * - `getSSOUrl` — `/idp/v1/Authentication/GetSocialLogInEndPoint`. v4 starts SSO
 *   with `startLogin()` in `services/blocks/sso.ts` (`/iam/v4/idp/initiate`).
 * - `storeTokens` — wrote a JS-held token pair; on web there is no readable token,
 *   the session is the HttpOnly cookie.
 * - `refreshTokens` — the v1 `grant_type=refresh_token` password grant. Renewal is
 *   `refreshSession()` in `sso.ts` (`POST /auth/refresh`, empty body), called by
 *   the axios / GraphQL 401 handlers.
 * - `switchOrganization` — a duplicate of `useSwitchOrgMutation`, which is now on
 *   `POST /auth/switch-org`. See `switchOrg` in `services/blocks/session.ts`.
 */
